import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, FileText, Loader2, Printer, Settings2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { API, api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Field } from "@/pages/Leads";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { EwayStatusDialog } from "@/components/DispatchActions";
import { EwayManagePanel } from "@/components/EwayManage";

const newKey = () => (crypto.randomUUID?.() || `k-${Date.now()}-${Math.random()}`);

function CheckRow({ item }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      {item.ok
        ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
        : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />}
      <div>
        <span className={item.ok ? "" : "font-medium"}>{item.label}</span>
        {!item.ok && <div className="text-[11px] text-muted-foreground">{item.hint}</div>}
      </div>
    </div>
  );
}

/** One-click generation: auto-filled from the challan, asks only for missing transport details. */
export function EwayGenerateDialog({ challan, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [idemKey, setIdemKey] = useState(newKey);
  const [success, setSuccess] = useState(null);
  const [manual, setManual] = useState(false);
  const [more, setMore] = useState(false);
  const [favs, setFavs] = useState({ vehicles: [], transporters: [] });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/eway/challans/${challan.id}/precheck`);
      setData(d);
      setForm(Object.fromEntries(d.transport_fields.map((f) => [f.field, f.value || ""])));
      const { data: f } = await api.get("/eway/favourites");
      setFavs(f);
    } catch (e) {
      toast.error(apiError(e));
      onClose();
    }
  }, [challan.id, onClose]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/eway/challans/${challan.id}/generate`, form,
        { headers: { "Idempotency-Key": idemKey } });
      setSuccess(r);
      toast.success(r.status === "already_generated"
        ? "This challan already has an e-way bill" : "E-way bill generated");
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
      setIdemKey(newKey());
      load();
    } finally {
      setBusy(false);
    }
  };

  if (manual) {
    return (
      <EwayStatusDialog challan={challan} onClose={onClose}
        onSaved={() => { onDone?.(); }} />
    );
  }

  const required = (data?.transport_fields || []).filter((f) => f.required);
  const missing = required.filter((f) => !String(form[f.field] || "").trim());
  const blocked = (data?.blocking || []).length > 0;
  const managed = !!data?.existing?.official;
  const shortcuts = { vehicle_number: favs.vehicles || [],
    transporter_name: favs.transporters || [] };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
        data-testid="eway-generate-dialog">
        <DialogHeader>
          <DialogTitle>E-Way Bill — {challan.number}</DialogTitle>
          <DialogDescription>
            {data?.configured
              ? "Everything is filled in from this challan. Add only what is missing."
              : "Generation needs an authorised e-way bill API connection."}
          </DialogDescription>
        </DialogHeader>

        {!data && (
          <div className="flex justify-center py-8" data-testid="eway-generate-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {data && success && (
          <div className="space-y-3" data-testid="eway-success-panel">
            <div className="surface space-y-1 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-600">
                <Check className="h-4 w-4" /> E-way bill generated successfully
              </div>
              <div className="font-mono-num text-2xl" data-testid="eway-success-number">
                {success.eway_number}
              </div>
              {success.mock && (
                <Badge variant="destructive" className="text-[10px]" data-testid="eway-mock-badge">
                  MOCK / SANDBOX — not an official government number
                </Badge>
              )}
              <div className="grid gap-1 pt-2 text-xs sm:grid-cols-2">
                {[["Generated on", success.eway_date || "—"],
                  ["Valid till", success.valid_till || "—"],
                  ["Vehicle", success.vehicle_number || "—"],
                  ["Transporter", success.transporter_name || "—"]].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <span className="text-muted-foreground">{k}</span>
                      <span className="font-medium">{v}</span>
                    </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="gap-1"
                data-testid="eway-success-print-pack"
                onClick={() => window.open(`${API}/challans/${challan.id}/dispatch-pack.pdf`,
                  "_blank")}>
                <Printer className="h-3.5 w-3.5" /> Print dispatch pack
              </Button>
              <Button size="sm" variant="outline" className="gap-1"
                data-testid="eway-success-challan-pdf"
                onClick={() => window.open(`${API}/challans/${challan.id}/pdf`, "_blank")}>
                <FileText className="h-3.5 w-3.5" /> Challan PDF
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={onClose} data-testid="eway-success-close">Done</Button>
            </DialogFooter>
          </div>
        )}

        {data && !success && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={data.configured ? "default" : "secondary"} className="text-[10px]"
                data-testid="eway-provider-badge">
                {data.provider_label}
              </Badge>
              <Badge variant="outline" className="text-[10px]" data-testid="eway-mode-badge">
                {data.mode}
              </Badge>
              {data.mock && (
                <Badge variant="destructive" className="text-[10px]"
                  data-testid="eway-simulator-badge">
                  simulator — numbers are not official
                </Badge>
              )}
            </div>

            {managed && (
              <EwayManagePanel challan={challan} precheck={data}
                onDone={() => { load(); onDone?.(); }} onClose={onClose} />
            )}

            {!managed && data.existing?.eway_number && (
              <div className="surface p-3 text-xs" data-testid="eway-existing-panel">
                Already recorded: <span className="font-mono-num">{data.existing.eway_number}</span>
                {" · "}{data.existing.eway_status || "Generated"}
                {data.existing.source ? ` · ${data.existing.source}` : ""}
              </div>
            )}

            {!managed && (
            <div className="surface grid gap-2 p-3 text-xs sm:grid-cols-2"
              data-testid="eway-summary-panel">
              {[["Document", data.summary.document],
                ["Document date", data.summary.document_date],
                ["Consignee", data.summary.consignee],
                ["Consignee GSTIN", data.summary.consignee_gstin],
                ["Place of supply", data.summary.place_of_supply],
                ["Invoice value", `₹${Number(data.summary.total_invoice_value)
                  .toLocaleString("en-IN")}`]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="text-right font-medium">{v || "—"}</span>
                  </div>
              ))}
            </div>
            )}

            {!managed && (
            <div className="space-y-1.5" data-testid="eway-precheck-list">
              {data.checks.map((c) => <CheckRow key={c.label} item={c} />)}
            </div>
            )}

            {!managed && !data.configured && (
              <div className="surface space-y-2 p-3 text-xs" data-testid="eway-not-configured">
                <p className="font-medium">E-Way API integration is not configured.</p>
                <p className="text-muted-foreground">
                  Connect an authorised GSP or the NIC API, or record the official number manually.
                </p>
                <div className="flex flex-wrap gap-2">
                  {data.is_owner && (
                    <Button asChild size="sm" variant="outline" className="gap-1"
                      data-testid="eway-configure-link">
                      <Link to="/eway-settings"><Settings2 className="h-3.5 w-3.5" /> Configure
                        integration</Link>
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setManual(true)}
                    data-testid="eway-record-manually-primary">
                    Record e-way bill manually
                  </Button>
                </div>
              </div>
            )}

            {!managed && data.configured && (
              <>
                <div className="grid gap-3 sm:grid-cols-2" data-testid="eway-transport-form">
                  {data.transport_fields.map((f) => (
                    <Field key={f.field}
                      label={`${f.label}${f.required ? " *" : ""}`}>
                      {f.type === "select" ? (
                        <Select value={form[f.field] || ""}
                          onValueChange={(v) => setForm({ ...form, [f.field]: v })}>
                          <SelectTrigger data-testid={`eway-field-${f.field}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {f.options.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <>
                          <Input type={f.type === "date" ? "date" : "text"}
                            data-testid={`eway-field-${f.field}`}
                            value={form[f.field] || ""}
                            onChange={(e) => setForm({ ...form, [f.field]: e.target.value })} />
                          {(shortcuts[f.field] || []).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1"
                              data-testid={`eway-shortcuts-${f.field}`}>
                              {shortcuts[f.field].map((s) => (
                                <button key={s.value} type="button"
                                  data-testid={`eway-shortcut-${f.field}-${s.value}`}
                                  className="rounded-full border px-2 py-0.5 text-[10px]
                                    transition-colors hover:bg-accent"
                                  onClick={() => setForm({ ...form, [f.field]: s.value })}>
                                  {s.value}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </Field>
                  ))}
                </div>

                <p className={`text-xs ${missing.length ? "text-amber-600"
                  : "text-muted-foreground"}`} data-testid="eway-missing-note">
                  {missing.length
                    ? `Still needed: ${missing.map((f) => f.label).join(", ")}`
                    : "All mandatory transport details are ready."}
                </p>

                <div className="border-t pt-3">
                  <Button variant="ghost" size="sm" className="gap-1 px-0 text-xs"
                    data-testid="eway-more-options" onClick={() => setMore(!more)}>
                    {more ? <X className="h-3 w-3" /> : null} More options
                  </Button>
                  {more && (
                    <Button size="sm" variant="outline" className="mt-2"
                      data-testid="eway-record-manually"
                      onClick={() => setManual(true)}>
                      Record e-way bill manually (portal entry)
                    </Button>
                  )}
                </div>
              </>
            )}

            {!managed && data.configured && (
              <DialogFooter>
                <Button disabled={busy || missing.length > 0 || blocked || !data.can_generate}
                  onClick={generate} data-testid="eway-generate-button">
                  {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Generate E-Way Bill
                </Button>
              </DialogFooter>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function EwayButton({ challan, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" className="gap-1"
        data-testid={`eway-generate-open-${challan.id}`}
        onClick={() => setOpen(true)}>
        <FileText className="h-3.5 w-3.5" /> E-Way
      </Button>
      {open && (
        <EwayGenerateDialog challan={challan} onClose={() => setOpen(false)} onDone={onDone} />
      )}
    </>
  );
}
